
import { useLanguage } from '@/src/contexts';
import { getText } from '@/src/helpers';
import { useOrderbookSubscription, useSelectedTokens } from '@/src/hooks';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Flex,
  Spinner,
  Table,
  TableBody,
  TableHeader,
  Text,
  TradingSection,
} from '@/src/ui-components';
import { Loader } from 'lucide-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { TradingFormValues } from '../../trading.types';
import { Offers, OrderbookHeader } from './components';
import { buildRequest } from './helpers';

export function OrderBook() {
  const { language } = useLanguage();
  const { watch } = useFormContext<TradingFormValues>();
  const { baseTokenId, quoteTokenId } = watch();
  const [baseToken, quoteToken] = useSelectedTokens(baseTokenId, quoteTokenId);

  const isValidPair = !!baseToken && !!quoteToken;

  const sellRequest = isValidPair ? buildRequest(baseToken, quoteToken) : null;
  const buyRequest = isValidPair ? buildRequest(quoteToken, baseToken) : null;

  // Use WebSocket subscriptions instead of polling
  const {
    data: sellOffers,
    isLoading: isLoadingSellOffers,
    isFetching: isFetchingSellOffers,
    refetch: refetchSellOffers,
    isError: isErrorSellOffers,
  } = useOrderbookSubscription(sellRequest, {
    enabled: isValidPair,
  });
  const {
    data: buyOffers,
    isLoading: isLoadingBuyOffers,
    isFetching: isFetchingBuyOffers,
    refetch: refetchBuyOffers,
    isError: isErrorBuyOffers,
  } = useOrderbookSubscription(buyRequest, {
    enabled: isValidPair,
  });

  const isLoading = isLoadingSellOffers || isLoadingBuyOffers;
  const isFetching = isFetchingSellOffers || isFetchingBuyOffers;

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <Empty className="w-full">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Spinner variant="circle" />
          </EmptyMedia>
          <EmptyTitle>Loading order book</EmptyTitle>
          <EmptyDescription>
            Fetching live orderbook data from XRPL network...
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const isError = isErrorSellOffers || isErrorBuyOffers;

  if (isError) {
    content = (
      <Flex justify="center" className="p-4">
        <Button
          onClick={() => {
            refetchSellOffers();
            refetchBuyOffers();
          }}
          disabled={isFetching}
        >
          {isFetching ? (
            <>
              <Loader className="h-4 w-4 animate-spin mr-2" />
              {getText(language, 'retry')}ing...
            </>
          ) : (
            getText(language, 'retry')
          )}
        </Button>
      </Flex>
    );
  }

  const hasTokens = baseToken && quoteToken;

  if (!hasTokens) {
    content = (
      <Text size="sm" className="text-muted-foreground">
        Invalid token pair
      </Text>
    );
  }

  const isValid = !isLoading && !isError && hasTokens;

  if (isValid) {
    content = (
      <Table>
        <TableHeader>
          <OrderbookHeader baseToken={baseToken} quoteToken={quoteToken} />
        </TableHeader>

        <TableBody>
          <Offers sellOffers={sellOffers?.offers || []} buyOffers={buyOffers?.offers || []} />
        </TableBody>
      </Table>
    );
  }

  return (
    <TradingSection>
      <TradingSection.Header>
        <TradingSection.Title>{getText(language, 'orderBook')}</TradingSection.Title>
      </TradingSection.Header>
      <TradingSection.Body className="p-1 overflow-y-auto">{content}</TradingSection.Body>
    </TradingSection>
  );
}
